import { useMemo, useState } from 'react';
import useSWR from 'swr';

import { newGridRecordId } from '@shared/commonFunctions';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SERVICES } from '@shared/constants/apiConfig';

import { Wrapper } from '@styles/CommonStyles';
import { stateTitleDocumentValidationSchema } from './constants';
import { IStateTitleDocument } from './interfaces';
import { useStateTitleDocumentColumns } from './hooks/useStateTitleColumns';
import { requestCreateStateTitleDocument, requestUpdateStateTitleDocument, requestDeleteStateTitleDocument } from './requests';

export const TitlesDVMPacket = () => {
  const { columns } = useStateTitleDocumentColumns();
  const [loading, setLoading] = useState(false);

  const { data: stateTitleDocuments, mutate: refetchStateTitleDocuments, isValidating, error } = useSWR(`/${SERVICES.DOCUMENT}/state-title-document`);

  const isLoading = useMemo(() =>
    (!stateTitleDocuments && !error) || isValidating || loading
    , [stateTitleDocuments, isValidating, error, loading]);

  const gridActions = useMemo(
    () => ({
      onSave: async (values: IStateTitleDocument) => {
        setLoading(true);
        if (values.id === newGridRecordId) {
          await requestCreateStateTitleDocument(values);
        } else {
          await requestUpdateStateTitleDocument(values);
        }

        await refetchStateTitleDocuments();
        setLoading(false);
      },
      onDelete: async (values: IStateTitleDocument) => {
        setLoading(true);
        await requestDeleteStateTitleDocument(values.id);
        await refetchStateTitleDocuments();
        setLoading(false);
      },
    }),
    [requestCreateStateTitleDocument, requestUpdateStateTitleDocument, refetchStateTitleDocuments]
  );

  return (
    <Wrapper>
      <PhoenixGrid
        autoHeight
        canNotCopy
        strict
        withHeader
        name="State Titles Documents"
        actions={gridActions}
        columns={columns}
        validateSchema={stateTitleDocumentValidationSchema}
        loading={isLoading}
        rows={isLoading ? [] : stateTitleDocuments}
      />
    </Wrapper>
  );
};
